import LmsSyncStatus from 'components/InstructorSubmissions/LmsSyncStatus';
import SemanticGradeDisplay from 'components/InstructorSubmissions/SemanticGradeDisplay';
import SubmissionSelectionCount from 'components/InstructorSubmissions/SubmissionSelectionCount';
import { useTranslation } from 'react-i18next';
import {
    Avatar,
    Button,
    Checkbox,
    Container,
    Divider,
    Icon,
    Paper,
    ScrollableTable,
    ScrollableTableBody,
    ScrollableTableCell,
    ScrollableTableHead,
    ScrollableTableRow,
    ScrollableTableSortableHeaderCells,
    Stack,
    styled,
    Text,
    TextInput,
} from 'shell/components';
import { Placeholder } from 'utils/constants';
import { getLatestPushbackRecord } from 'utils/gradePushback';
import { getLatestConfirmedId, getSubmissionGradingTime, getUserPreferredFullName } from 'utils/helper';
import {
    getQuizSubmissionGradeSnapshot,
    getQuizSubmissionSection,
    getQuizSubmissionStatus,
    QuizSubmissionStatus,
} from 'utils/quizSubmissionList';

const SubmissionStatus = ({ submission }) => {
    const { t } = useTranslation();
    const statuses = {
        [QuizSubmissionStatus.REVIEWED]: { icon: 'checkmark', label: t('status.reviewed', 'Reviewed') },
        [QuizSubmissionStatus.READY]: { icon: 'clock', label: t('status.ready-for-review', 'Ready for review') },
        [QuizSubmissionStatus.IN_PROGRESS]: {
            icon: 'clock',
            label: t('status.partially-reviewed', 'Partially reviewed'),
        },
        [QuizSubmissionStatus.FAILED]: { icon: 'error', label: t('status.failed-to-process', 'Failed to process') },
        [QuizSubmissionStatus.PROCESSING]: { icon: 'clock', label: t('ui.processing', 'Processing') },
    };
    const { icon, label } = statuses[getQuizSubmissionStatus(submission)];
    return (
        <Stack direction={'row'} spacing={2} alignItems={'center'}>
            <Icon kind={icon} />
            <Text>{label}</Text>
        </Stack>
    );
};

const QuizSubmissionsLayout = ({
    device,
    quiz,
    submissions,
    accountUsers,
    sortedFilteredSubmissions,
    columns,
    checked,
    setChecked,
    filters,
    setFilters,
    sortState,
    handleSetSortState,
    canReset,
    resetting,
    resetSubmissions,
    exportSubmissions,
    canPushGrades,
    pushGrades,
    onClickBack,
    onClickViewAll,
    onClickViewSubmission,
}) => {
    const { t } = useTranslation('quiz');
    const mobile = device === 'mobile';
    const filterCount =
        Number(Boolean(sortState.orderBy)) +
        ['status', 'lmsSyncStatus', 'gradeRecord'].filter((key) => filters[key].length).length;
    const allSelected =
        sortedFilteredSubmissions.length > 0 && sortedFilteredSubmissions.every(({ id }) => checked.includes(id));
    const toggleAll = () =>
        setChecked((current) =>
            allSelected
                ? current.filter((id) => !sortedFilteredSubmissions.some((submission) => submission.id === id))
                : [...new Set([...current, ...sortedFilteredSubmissions.map(({ id }) => id)])]
        );
    const toggleSubmission = (id) =>
        setChecked((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
    const hasReviewed = sortedFilteredSubmissions.some(
        (submission) => getQuizSubmissionStatus(submission) === QuizSubmissionStatus.REVIEWED
    );
    const visibleColumns = columns.filter(
        ({ id }) => quiz.ai_grading_enabled || !['grade', 'lms-sync-status', 'last-pushed-grade'].includes(id)
    );

    const renderCell = (submission, id) => {
        const reviewed = getQuizSubmissionStatus(submission) === QuizSubmissionStatus.REVIEWED;
        switch (id) {
            case 'identifier':
                return submission.api?.user?.id ?? Placeholder.NO_DATA;
            case 'name':
                return submission.api?.user?.name || t('submissions.unknown-student', 'Student');
            case 'section':
                return getQuizSubmissionSection(submission);
            case 'status':
                return <SubmissionStatus submission={submission} />;
            case 'grade':
                return <SemanticGradeDisplay snapshot={getQuizSubmissionGradeSnapshot(submission, quiz)} />;
            case 'lms-sync-status':
                return reviewed ? <LmsSyncStatus submission={submission} assignment={quiz} /> : Placeholder.NO_DATA;
            case 'last-pushed-grade':
                return <SemanticGradeDisplay snapshot={getLatestPushbackRecord(submission)} />;
            case 'time':
                return getSubmissionGradingTime(submission);
            case 'grader': {
                const grader =
                    reviewed &&
                    accountUsers.find(
                        ({ owner_account_id }) => owner_account_id === getLatestConfirmedId(submission.sessions)
                    );
                return grader ? (
                    <Stack direction={'row'} alignItems={'center'} spacing={2}>
                        <Avatar user={grader} />
                        {getUserPreferredFullName(grader)}
                    </Stack>
                ) : (
                    Placeholder.NO_DATA
                );
            }
            default:
                return Placeholder.NO_DATA;
        }
    };

    const viewButton = (submission) => (
        <Button
            disabled={getQuizSubmissionStatus(submission) !== QuizSubmissionStatus.REVIEWED}
            variant={'borderless'}
            onClick={() => onClickViewSubmission(submission)}
        >
            {t('common:controls.view', 'View')}
        </Button>
    );
    const selectCheckbox = (submission) => (
        <Checkbox
            aria-label={t('submissions.select-student', 'Select submission for {{student}}', {
                student:
                    submission.api?.user?.name ||
                    submission.api?.user?.id ||
                    t('submissions.unknown-student', 'Student'),
            })}
            checked={checked.includes(submission.id)}
            onChange={() => toggleSubmission(submission.id)}
        />
    );

    const pushButton = (
        <Button
            fullWidth={mobile}
            disabled={!canPushGrades || resetting}
            startIcon={<Icon kind={'arrowRight'} />}
            tooltip={
                process.env.REACT_APP_KIND === 'D2L'
                    ? { component: t('submissions.push-unavailable', 'Grade pass-back is unavailable for your LMS.') }
                    : undefined
            }
            onClick={pushGrades}
        >
            {t('common:controls.push-to-lms', 'Push to LMS')}
        </Button>
    );
    const actions = (
        <Stack direction={mobile ? 'column' : 'row'} spacing={3} alignItems={'center'} sx={{ flexShrink: 0 }}>
            {mobile && pushButton}
            <Stack
                direction={'row'}
                spacing={3}
                justifyContent={mobile ? 'space-around' : undefined}
                sx={{ width: mobile ? '100%' : undefined }}
            >
                {checked.length > 0 && (
                    <Button color={'neutral'} variant={'tertiary'} onClick={() => setChecked([])}>
                        {t('common:controls.cancel', 'Cancel')}
                    </Button>
                )}
                <Button
                    disabled={!canReset}
                    loading={resetting}
                    color={'destructive'}
                    variant={mobile ? 'tertiary' : 'secondary'}
                    startIcon={<Icon kind={'refresh'} />}
                    onClick={resetSubmissions}
                >
                    {t('common:controls.reset', 'Reset')}
                </Button>
                <Button
                    disabled={!submissions.length || resetting}
                    variant={mobile ? 'tertiary' : 'secondary'}
                    startIcon={<Icon kind={'download'} />}
                    onClick={exportSubmissions}
                >
                    {t('common:controls.export', 'Export')}
                </Button>
            </Stack>
            {!mobile && pushButton}
        </Stack>
    );
    const viewAll = (
        <Button disabled={!hasReviewed} variant={'borderless'} onClick={onClickViewAll}>
            {t('common:controls.view-all', 'View All')}
        </Button>
    );

    return (
        <Container device={device} sx={{ height: mobile ? 'fit-content' : '100%' }}>
            <Stack
                spacing={5}
                sx={{ width: '100%', minHeight: mobile ? undefined : '100%', pb: mobile ? 5 : 0 }}
                aria-busy={resetting}
            >
                <Button
                    sx={{ alignSelf: 'flex-start' }}
                    startIcon={<Icon kind={'arrowLeft'} />}
                    variant={'borderless'}
                    onClick={onClickBack}
                >
                    {t('common:controls.back', 'Back')}
                </Button>
                <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                    {quiz.title || t('create.untitled', 'Untitled Quiz')}
                </Text>
                <Text component={'h2'} variant={'sh1'} weight={'semibold'}>
                    {t('submissions.list-title', 'Submissions')}
                </Text>
                {mobile && actions}
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    spacing={4}
                    sx={{ flexWrap: 'wrap', rowGap: 3 }}
                >
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        spacing={4}
                        sx={{ width: mobile ? '100%' : undefined }}
                    >
                        <Button
                            sx={{ flexShrink: 0 }}
                            aria-label={t('common:controls.sort-filter', 'Sort & Filter')}
                            variant={'secondary'}
                            color={'neutral'}
                            startIcon={<Icon kind={'filter'} />}
                            onClick={() => setFilters((current) => ({ ...current, open: true }))}
                        >
                            {!mobile && t('common:controls.sort-filter', 'Sort & Filter')} ({filterCount})
                        </Button>
                        <TextInput
                            sx={{ width: mobile ? '100%' : '216px' }}
                            inputProps={{ 'aria-label': t('submissions.search-label', 'Search quiz submissions') }}
                            placeholder={t('common:controls.search', 'Search')}
                            endIcon={<Icon kind={'search'} />}
                            value={filters.search}
                            onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
                        />
                    </Stack>
                    {!mobile && actions}
                </Stack>
                <SubmissionSelectionCount count={checked.length} />
                {sortedFilteredSubmissions.length === 0 ? (
                    <Text role={'status'}>
                        {t('submissions.no-results', 'No submissions match your search or filters.')}
                    </Text>
                ) : mobile ? (
                    <>
                        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                            <Checkbox
                                aria-label={t('submissions.select-all', 'Select all visible submissions')}
                                checked={allSelected}
                                indeterminate={checked.length > 0 && !allSelected}
                                onChange={toggleAll}
                                label={t('assignment:grades-feedback', 'Grades/Feedback')}
                            />
                            {viewAll}
                        </Stack>
                        <Stack spacing={4}>
                            {sortedFilteredSubmissions.map((submission) => (
                                <Paper key={submission.id} variant={'outlined'} sx={{ p: 3, borderRadius: '12px' }}>
                                    <Stack spacing={3}>
                                        <Stack direction={'row'} alignItems={'center'} spacing={2}>
                                            {selectCheckbox(submission)}
                                            <Text component={'h3'} variant={'p1'} weight={'semibold'} sx={{ flex: 1 }}>
                                                {renderCell(submission, 'name')}
                                            </Text>
                                            {viewButton(submission)}
                                        </Stack>
                                        <Text color={'light'}>
                                            {t('common:table.identifier', 'Identifier')}:{' '}
                                            {renderCell(submission, 'identifier')}
                                        </Text>
                                        <Text color={'light'}>
                                            {t('common:table.section', 'Section')}: {renderCell(submission, 'section')}
                                        </Text>
                                        <Divider />
                                        {visibleColumns
                                            .filter(({ id }) => !['identifier', 'name', 'section'].includes(id))
                                            .map((column) => (
                                                <Stack
                                                    key={column.id}
                                                    direction={'row'}
                                                    alignItems={'center'}
                                                    spacing={2}
                                                    sx={{ flexWrap: 'wrap' }}
                                                >
                                                    <Text color={'light'}>{column.columnTitle}:</Text>
                                                    {renderCell(submission, column.id)}
                                                </Stack>
                                            ))}
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    </>
                ) : (
                    <SubmissionsTable aria-label={t('submissions.list-label', 'Quiz submissions')}>
                        <ScrollableTableHead>
                            <ScrollableTableRow>
                                <ScrollableTableCell isFixed>
                                    <Checkbox
                                        aria-label={t('submissions.select-all', 'Select all visible submissions')}
                                        checked={allSelected}
                                        indeterminate={checked.length > 0 && !allSelected}
                                        onChange={toggleAll}
                                    />
                                </ScrollableTableCell>
                                <ScrollableTableSortableHeaderCells
                                    columns={visibleColumns}
                                    sortState={sortState}
                                    handleSetSortState={handleSetSortState}
                                />
                                <ScrollableTableCell isFixed>
                                    <Stack
                                        direction={'row'}
                                        alignItems={'center'}
                                        justifyContent={'space-between'}
                                        spacing={4}
                                    >
                                        <Text>{t('assignment:grades-feedback', 'Grades/Feedback')}</Text>
                                        {viewAll}
                                    </Stack>
                                </ScrollableTableCell>
                            </ScrollableTableRow>
                        </ScrollableTableHead>
                        <ScrollableTableBody>
                            {sortedFilteredSubmissions.map((submission) => (
                                <ScrollableTableRow key={submission.id}>
                                    <ScrollableTableCell isFixed>{selectCheckbox(submission)}</ScrollableTableCell>
                                    {visibleColumns.map((column) => (
                                        <ScrollableTableCell key={column.id} isFixed={column.isFixed}>
                                            {renderCell(submission, column.id)}
                                        </ScrollableTableCell>
                                    ))}
                                    <ScrollableTableCell isFixed>{viewButton(submission)}</ScrollableTableCell>
                                </ScrollableTableRow>
                            ))}
                        </ScrollableTableBody>
                    </SubmissionsTable>
                )}
            </Stack>
        </Container>
    );
};

const SubmissionsTable = styled(ScrollableTable)(({ theme }) => ({
    flex: '1',
    '& .MuiTableCell-head': {
        whiteSpace: 'nowrap',
        wordBreak: 'normal',
        overflowWrap: 'normal',
        hyphens: 'none',
        '& .MuiTypography-root': {
            whiteSpace: 'nowrap',
            wordBreak: 'normal',
            overflowWrap: 'normal',
            flexShrink: 0,
        },
    },
    '& .MuiTableCell-root': {
        padding: theme.spacing(3),
        '&:first-child': {
            left: 0,
            width: '74px',
            minWidth: '74px',
            maxWidth: '74px',
            padding: theme.spacing(4),
        },
        '&:nth-child(2)': { left: '74px', width: '192px', minWidth: '192px' },
        '&:nth-child(3)': { minWidth: '192px' },
        '&:last-child': {
            right: 0,
            minWidth: '212px',
            borderLeft: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        },
    },
}));

export default QuizSubmissionsLayout;
